import React, { useEffect, useMemo, useState } from 'react'
import { AlignCenter, AlignLeft, AlignRight, Bold, Italic, Strikethrough, Underline, X } from 'lucide-react'
import type { CellFormat, GridController } from '../../compositor/GridController'
import {
  borderPatchForActiveSide,
  buildBorderPresetPatch,
  buildFormatCellsPreviewStyle,
  FORMAT_CELLS_FONT_SIZES,
  FORMAT_CELLS_TABS,
  formatCellsSection,
  formatNumberSample,
  getActiveBorderSection,
  mergeFormatCellsDraft,
  NUMBER_FORMAT_CATEGORY_OPTIONS,
  NUMBER_FORMAT_CURRENCY_SYMBOL_OPTIONS,
  NUMBER_FORMAT_LOCALE_OPTIONS,
  NUMBER_FORMAT_NEGATIVE_OPTIONS,
  NUMBER_FORMAT_TYPE_PRESETS,
  numberFormatPresetForCategory,
  SPECIAL_NUMBER_FORMATS,
  toFormatCellsColorInputValue,
  type FormatCellsBorderPreset,
  type FormatCellsTab,
} from '../../spreadsheet/formatCellsPresentation'
import { getSupportedFontFamilyOptions } from '../../tableau/fontRegistry'
import './ExcelWorkbookChrome.css'

type ExcelFormatCellsDialogProps = {
  controller: GridController
  currentFormat?: CellFormat
  onClose: () => void
  onApplied?: () => void
}

export function ExcelFormatCellsDialog({
  controller,
  currentFormat,
  onClose,
  onApplied,
}: ExcelFormatCellsDialogProps) {
  const [draft, setDraft] = useState<CellFormat>(() => currentFormat ?? {})
  const [activeTab, setActiveTab] = useState<FormatCellsTab>('number')
  const fontOptions = useMemo(() => getSupportedFontFamilyOptions(['table', 'ui']), [])

  useEffect(() => {
    setDraft(currentFormat ?? {})
  }, [currentFormat])

  const applyPatch = (patch: Partial<CellFormat>) => {
    setDraft((previous) => mergeFormatCellsDraft(previous, patch) as CellFormat)
    controller.applyFormatToSelection(patch)
    onApplied?.()
  }
  const replaceNumberFormat = (numberFormat: Record<string, unknown>) => {
    setDraft((previous) => ({ ...previous, numberFormat }))
    controller.applyFormatToSelection({ numberFormat })
    onApplied?.()
  }
  const applyProtectionPatch = (patch: Record<string, unknown>) => {
    const nextProtection = { ...formatCellsSection(draft, 'protection'), ...patch }
    setDraft((previous) => mergeFormatCellsDraft(previous, { protection: nextProtection }) as CellFormat)
    controller.setProtectionOnSelection(nextProtection)
    onApplied?.()
  }

  const clearFormats = () => {
    setDraft({})
    controller.clearFormatsFromSelection()
    onApplied?.()
  }

  const applyNumberCategory = (category: string) => {
    replaceNumberFormat(numberFormatPresetForCategory(category, formatCellsSection(draft, 'numberFormat')))
  }

  const numberFormat = formatCellsSection(draft, 'numberFormat')
  const numberCategory = String(numberFormat.category ?? 'general')
  const numberFormatTypePresets = NUMBER_FORMAT_TYPE_PRESETS[numberCategory] ?? []
  const font = formatCellsSection(draft, 'font')
  const fill = formatCellsSection(draft, 'fill')
  const border = formatCellsSection(draft, 'border')
  const alignment = formatCellsSection(draft, 'alignment')
  const protection = formatCellsSection(draft, 'protection')
  const previewStyle = buildFormatCellsPreviewStyle(draft) as React.CSSProperties
  const previewText = formatNumberSample(numberFormat)

  const fontFamily = String(font.family ?? draft.fontFamily ?? '')
  const fontSize = Number(font.size ?? draft.fontSize ?? '')
  const textColor = toFormatCellsColorInputValue(font.color ?? draft.textColor, '#111827')
  const fillBackgroundColor = toFormatCellsColorInputValue(fill.backgroundColor ?? fill.foregroundColor ?? draft.backgroundColor, '#ffffff')
  const fillPatternColor = toFormatCellsColorInputValue(fill.patternColor ?? fill.color, '#808080')
  const fontScript = String(font.script ?? 'normal')
  const underlineKind = String(font.underline ?? draft.underline ?? 'none')
  const horizontal = String(alignment.horizontal ?? draft.align ?? 'left')
  const vertical = String(alignment.vertical ?? draft.verticalAlign ?? 'bottom') === 'middle'
    ? 'center'
    : String(alignment.vertical ?? draft.verticalAlign ?? 'bottom')
  const textRotation = Number(alignment.textRotation ?? draft.textRotation ?? 0)
  const textOrientation = textRotation === 255
    ? 'vertical'
    : textRotation === 90
      ? 'rotateUp'
      : textRotation === -90
        ? 'rotateDown'
        : 'horizontal'
  const activeBorderSide = String(border.activeSide ?? 'bottom')
  const activeBorder = getActiveBorderSection(border, activeBorderSide)

  const applyBorderPreset = (
    preset: FormatCellsBorderPreset,
  ) => {
    applyPatch({ border: buildBorderPresetPatch({ preset, activeBorderSide, activeBorder }) })
  }

  const renderNumber = () => (
    <div className="fg-excel-format-grid">
      <label>
        <span>Category</span>
        <select
          value={numberCategory}
          aria-label="Number format category"
          title="Number format category"
          onChange={(event) => applyNumberCategory(event.target.value)}
        >
          {NUMBER_FORMAT_CATEGORY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Locale</span>
        <select
          value={String(numberFormat.locale ?? 'en-US')}
          aria-label="Number format locale"
          title="Number format locale"
          onChange={(event) => applyPatch({ numberFormat: { locale: event.target.value } })}
        >
          {NUMBER_FORMAT_LOCALE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Decimals</span>
        <input
          type="number"
          min={0}
          max={30}
          value={Number(numberFormat.decimalPlaces ?? 2)}
          aria-label="Decimal places"
          title="Decimal places"
          onChange={(event) => applyPatch({ numberFormat: { decimalPlaces: Number(event.target.value) } })}
        />
      </label>
      <label>
        <span>Symbol</span>
        <select
          value={String(numberFormat.currencySymbol ?? '$')}
          aria-label="Currency symbol"
          title="Currency symbol"
          onChange={(event) => applyPatch({ numberFormat: { currencySymbol: event.target.value } })}
        >
          {NUMBER_FORMAT_CURRENCY_SYMBOL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Negative</span>
        <select
          value={String(numberFormat.negativeNumbers ?? 'minus')}
          aria-label="Negative number style"
          title="Negative number style"
          onChange={(event) => applyPatch({ numberFormat: { negativeNumbers: event.target.value } })}
        >
          {NUMBER_FORMAT_NEGATIVE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Custom</span>
        <input
          value={String(numberFormat.code ?? '')}
          aria-label="Custom number format code"
          title="Custom number format code"
          onChange={(event) => applyPatch({ numberFormat: { category: 'custom', code: event.target.value } })}
        />
      </label>
      {numberFormatTypePresets.length > 0 ? (
        <label>
          <span>Type</span>
          <select
            value={String(numberFormat.code ?? numberFormatTypePresets[0]?.code ?? '')}
            aria-label="Number format type"
            title="Number format type"
            onChange={(event) => applyPatch({ numberFormat: { category: numberCategory, code: event.target.value } })}
          >
            {numberFormatTypePresets.map((entry) => (
              <option key={entry.code} value={entry.code}>{entry.label}</option>
            ))}
          </select>
        </label>
      ) : null}
      {numberCategory === 'special' ? (
        <label>
          <span>Type</span>
          <select
            value={String(numberFormat.code ?? '00000')}
            aria-label="Special number format type"
            title="Special number format type"
            onChange={(event) => applyPatch({ numberFormat: { category: 'special', code: event.target.value } })}
          >
            {SPECIAL_NUMBER_FORMATS.map((entry) => (
              <option key={entry.code} value={entry.code}>{entry.label}</option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="fg-excel-format-check">
        <input
          type="checkbox"
          checked={numberFormat.useThousandsSeparator === true}
          aria-label="Use thousands separator"
          title="Use thousands separator"
          onChange={(event) => applyPatch({ numberFormat: { useThousandsSeparator: event.target.checked } })}
        />
        <span>Thousands separator</span>
      </label>
    </div>
  )

  const renderAlignment = () => (
    <div className="fg-excel-format-grid">
      <div className="fg-excel-format-button-row" aria-label="Horizontal alignment">
        {[
          ['left', AlignLeft, 'Align Left'],
          ['center', AlignCenter, 'Align Center'],
          ['right', AlignRight, 'Align Right'],
        ].map(([value, Icon, label]) => (
          <button
            key={String(value)}
            type="button"
            className="fg-excel-icon-button"
            data-active={horizontal === value ? 'true' : 'false'}
            aria-pressed={horizontal === value}
            aria-label={String(label)}
            title={String(label)}
            onClick={() => applyPatch({ alignment: { horizontal: value }, align: value as CellFormat['align'] })}
          >
            {React.createElement(Icon as typeof AlignLeft, { size: 16 })}
          </button>
        ))}
      </div>
      <label>
        <span>Horizontal</span>
        <select
          value={horizontal}
          aria-label="Horizontal alignment"
          title="Horizontal alignment"
          onChange={(event) => applyPatch({ alignment: { horizontal: event.target.value }, align: event.target.value as CellFormat['align'] })}
        >
          <option value="general">General</option>
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="fill">Fill</option>
          <option value="justify">Justify</option>
          <option value="centerContinuous">Center Across Selection</option>
          <option value="distributed">Distributed</option>
        </select>
      </label>
      <label>
        <span>Vertical</span>
        <select
          value={vertical}
          aria-label="Vertical alignment"
          title="Vertical alignment"
          onChange={(event) => applyPatch({ alignment: { vertical: event.target.value }, verticalAlign: event.target.value as CellFormat['verticalAlign'] })}
        >
          <option value="top">Top</option>
          <option value="center">Center</option>
          <option value="bottom">Bottom</option>
          <option value="justify">Justify</option>
          <option value="distributed">Distributed</option>
        </select>
      </label>
      <label className="fg-excel-format-check">
        <input
          type="checkbox"
          checked={alignment.wrapText === true || draft.wrapText === true}
          aria-label="Wrap text"
          title="Wrap text"
          onChange={(event) => applyPatch({ alignment: { wrapText: event.target.checked }, wrapText: event.target.checked })}
        />
        <span>Wrap text</span>
      </label>
      <label className="fg-excel-format-check">
        <input
          type="checkbox"
          checked={alignment.shrinkToFit === true || draft.shrinkToFit === true}
          aria-label="Shrink to fit"
          title="Shrink to fit"
          onChange={(event) => applyPatch({ alignment: { shrinkToFit: event.target.checked }, shrinkToFit: event.target.checked })}
        />
        <span>Shrink to fit</span>
      </label>
      <label>
        <span>Indent</span>
        <input
          type="number"
          min={0}
          max={15}
          value={Number(alignment.indent ?? draft.indent ?? 0)}
          aria-label="Indent"
          title="Indent"
          onChange={(event) => applyPatch({ alignment: { indent: Number(event.target.value) }, indent: Number(event.target.value) })}
        />
      </label>
      <label>
        <span>Orientation</span>
        <select
          value={textOrientation}
          aria-label="Text orientation"
          title="Text orientation"
          onChange={(event) => {
            const rotation = event.target.value === 'vertical'
              ? 255
              : event.target.value === 'rotateUp'
                ? 90
                : event.target.value === 'rotateDown'
                  ? -90
                  : 0
            applyPatch({ alignment: { textRotation: rotation }, textRotation: rotation })
          }}
        >
          <option value="horizontal">Horizontal</option>
          <option value="rotateUp">Rotate Text Up</option>
          <option value="rotateDown">Rotate Text Down</option>
          <option value="vertical">Vertical Text</option>
        </select>
      </label>
      <label>
        <span>Rotation</span>
        <input
          type="number"
          min={-90}
          max={255}
          value={textRotation}
          aria-label="Text rotation"
          title="Text rotation"
          onChange={(event) => {
            const raw = Number(event.target.value)
            const rotation = raw === 255 ? 255 : Math.max(-90, Math.min(90, raw))
            applyPatch({ alignment: { textRotation: rotation }, textRotation: rotation })
          }}
        />
      </label>
      <label>
        <span>Direction</span>
        <select
          value={String(alignment.readingOrder ?? 'context')}
          aria-label="Reading order"
          title="Reading order"
          onChange={(event) => applyPatch({ alignment: { readingOrder: event.target.value } })}
        >
          <option value="context">Context</option>
          <option value="leftToRight">Left-to-right</option>
          <option value="rightToLeft">Right-to-left</option>
        </select>
      </label>
    </div>
  )

  const renderFont = () => (
    <div className="fg-excel-format-grid">
      <label>
        <span>Font</span>
        <select
          value={fontFamily}
          aria-label="Font"
          title="Font"
          onChange={(event) => applyPatch({ font: { family: event.target.value || undefined }, fontFamily: event.target.value || undefined })}
        >
          <option value="">Default</option>
          {fontOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Size</span>
        <select
          value={Number.isFinite(fontSize) && fontSize > 0 ? fontSize : ''}
          aria-label="Font size"
          title="Font size"
          onChange={(event) => applyPatch({ font: { size: Number(event.target.value) || undefined }, fontSize: Number(event.target.value) || undefined })}
        >
          <option value="">Default</option>
          {FORMAT_CELLS_FONT_SIZES.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>
      <div className="fg-excel-format-button-row" aria-label="Font style">
        {[
          ['bold', Bold, 'Bold'],
          ['italic', Italic, 'Italic'],
          ['underline', Underline, 'Underline'],
          ['strikethrough', Strikethrough, 'Strikethrough'],
        ].map(([key, Icon, label]) => {
          const enabled = key === 'underline'
            ? Boolean(font.underline ?? draft.underline)
            : Boolean(font[String(key)] ?? draft[String(key)])
          return (
            <button
              key={String(key)}
              type="button"
              className="fg-excel-icon-button"
              data-active={enabled ? 'true' : 'false'}
              aria-pressed={enabled}
              aria-label={String(label)}
              title={String(label)}
              onClick={() => applyPatch({ font: { [String(key)]: !enabled }, [String(key)]: !enabled })}
            >
              {React.createElement(Icon as typeof Bold, { size: 16 })}
            </button>
          )
        })}
      </div>
      <label>
        <span>Underline</span>
        <select
          value={underlineKind === 'true' ? 'single' : underlineKind === 'false' ? 'none' : underlineKind}
          aria-label="Underline style"
          title="Underline style"
          onChange={(event) => {
            const value = event.target.value === 'none' ? false : event.target.value
            applyPatch({ font: { underline: value }, underline: value })
          }}
        >
          <option value="none">None</option>
          <option value="single">Single</option>
          <option value="double">Double</option>
          <option value="singleAccounting">Single Accounting</option>
          <option value="doubleAccounting">Double Accounting</option>
        </select>
      </label>
      <label>
        <span>Text</span>
        <input
          type="color"
          value={textColor}
          aria-label="Text color"
          title="Text color"
          onChange={(event) => applyPatch({ font: { color: event.target.value }, textColor: event.target.value })}
        />
      </label>
      <label>
        <span>Script</span>
        <select
          value={fontScript}
          aria-label="Font script"
          title="Font script"
          onChange={(event) => applyPatch({ font: { script: event.target.value === 'normal' ? undefined : event.target.value } })}
        >
          <option value="normal">Normal</option>
          <option value="superscript">Superscript</option>
          <option value="subscript">Subscript</option>
        </select>
      </label>
    </div>
  )

  const renderBorder = () => (
    <div className="fg-excel-format-grid">
      <div className="fg-excel-format-button-row" aria-label="Border presets">
        <button type="button" title="No border" onClick={() => applyBorderPreset('none')}>None</button>
        <button type="button" title="Outline border" onClick={() => applyBorderPreset('outline')}>Outline</button>
        <button type="button" title="Inside borders" onClick={() => applyBorderPreset('inside')}>Inside</button>
        <button type="button" title="All borders" onClick={() => applyBorderPreset('all')}>All</button>
        <button type="button" title="Thick outside border" onClick={() => applyBorderPreset('thickOutline')}>Thick Outside</button>
        <button type="button" title="Bottom border" onClick={() => applyBorderPreset('bottom')}>Bottom</button>
        <button type="button" title="Top and bottom border" onClick={() => applyBorderPreset('topBottom')}>Top/Bottom</button>
        <button type="button" title="Top and thick bottom border" onClick={() => applyBorderPreset('topThickBottom')}>Top + Thick Bottom</button>
        <button type="button" title="Top and double bottom border" onClick={() => applyBorderPreset('topDoubleBottom')}>Top + Double Bottom</button>
      </div>
      <label>
        <span>Side</span>
        <select
          value={activeBorderSide}
          aria-label="Border side"
          title="Border side"
          onChange={(event) => applyPatch({ border: { activeSide: event.target.value } })}
        >
          <option value="top">Top</option>
          <option value="bottom">Bottom</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="inside">Inside</option>
          <option value="diagonal">Diagonal Both</option>
          <option value="diagonalUp">Diagonal Up</option>
          <option value="diagonalDown">Diagonal Down</option>
        </select>
      </label>
      <label>
        <span>Line</span>
        <select
          value={String(activeBorder.style ?? 'thin')}
          aria-label="Border line style"
          title="Border line style"
          onChange={(event) => {
            const nextSide = { ...activeBorder, style: event.target.value }
            applyPatch({ border: borderPatchForActiveSide(activeBorderSide, nextSide) })
          }}
        >
          <option value="none">None</option>
          <option value="thin">Thin</option>
          <option value="medium">Medium</option>
          <option value="thick">Thick</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
          <option value="hair">Hairline</option>
          <option value="mediumDashed">Medium dashed</option>
          <option value="dashDot">Dash dot</option>
          <option value="mediumDashDot">Medium dash dot</option>
          <option value="dashDotDot">Dash dot dot</option>
          <option value="mediumDashDotDot">Medium dash dot dot</option>
          <option value="slantDashDot">Slant dash dot</option>
          <option value="double">Double</option>
        </select>
      </label>
      <label>
        <span>Color</span>
        <input
          type="color"
          value={toFormatCellsColorInputValue(activeBorder.color, '#808080')}
          aria-label="Border color"
          title="Border color"
          onChange={(event) => {
            const nextSide = { ...activeBorder, color: event.target.value }
            applyPatch({ border: borderPatchForActiveSide(activeBorderSide, nextSide) })
          }}
        />
      </label>
    </div>
  )

  const renderFill = () => (
    <div className="fg-excel-format-grid">
      <div className="fg-excel-format-button-row" aria-label="Fill presets">
        <button
          type="button"
          title="No fill"
          onClick={() => applyPatch({
            fill: { kind: 'none', foregroundColor: undefined, backgroundColor: undefined, patternKind: 'none' },
            backgroundColor: undefined,
          })}
        >
          No Fill
        </button>
        <button
          type="button"
          title="Solid fill"
          onClick={() => applyPatch({
            fill: { kind: 'solid', patternKind: 'solid', foregroundColor: fillBackgroundColor, backgroundColor: fillBackgroundColor },
            backgroundColor: fillBackgroundColor,
          })}
        >
          Solid
        </button>
      </div>
      <label>
        <span>Background</span>
        <input
          type="color"
          value={fillBackgroundColor}
          aria-label="Fill background color"
          title="Fill background color"
          onChange={(event) => applyPatch({
            fill: { kind: 'solid', foregroundColor: event.target.value, backgroundColor: event.target.value },
            backgroundColor: event.target.value,
          })}
        />
      </label>
      <label>
        <span>Pattern</span>
        <select
          value={String(fill.patternKind ?? 'none')}
          aria-label="Fill pattern"
          title="Fill pattern"
          onChange={(event) => applyPatch({ fill: { patternKind: event.target.value } })}
        >
          <option value="none">None</option>
          <option value="gray0625">Gray 6.25%</option>
          <option value="gray125">Gray 12.5%</option>
          <option value="gray25">Gray 25%</option>
          <option value="gray50">Gray 50%</option>
          <option value="gray75">Gray 75%</option>
          <option value="lightGray">Light gray</option>
          <option value="mediumGray">Medium gray</option>
          <option value="darkGray">Dark gray</option>
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
          <option value="reverseDiagonal">Reverse diagonal</option>
          <option value="diagonalCross">Diagonal cross</option>
          <option value="horizontalCross">Horizontal cross</option>
          <option value="lightHorizontal">Light horizontal</option>
          <option value="lightVertical">Light vertical</option>
          <option value="lightDown">Light down</option>
          <option value="lightUp">Light up</option>
          <option value="lightGrid">Light grid</option>
          <option value="lightTrellis">Light trellis</option>
          <option value="darkHorizontal">Dark horizontal</option>
          <option value="darkVertical">Dark vertical</option>
          <option value="darkDown">Dark down</option>
          <option value="darkUp">Dark up</option>
          <option value="darkGrid">Dark grid</option>
          <option value="darkTrellis">Dark trellis</option>
          <option value="solid">Solid</option>
        </select>
      </label>
      <label>
        <span>Pattern color</span>
        <input
          type="color"
          value={fillPatternColor}
          aria-label="Fill pattern color"
          title="Fill pattern color"
          onInput={(event) => applyPatch({ fill: { patternColor: event.currentTarget.value } })}
          onChange={(event) => applyPatch({ fill: { patternColor: event.target.value } })}
        />
      </label>
    </div>
  )

  const renderProtection = () => (
    <div className="fg-excel-format-grid">
      <label className="fg-excel-format-check">
        <input
          type="checkbox"
          checked={protection.locked !== false}
          aria-label="Locked"
          title="Locked"
          onChange={(event) => applyProtectionPatch({ locked: event.target.checked })}
        />
        <span>Locked</span>
      </label>
      <label className="fg-excel-format-check">
        <input
          type="checkbox"
          checked={protection.hiddenFormula === true || protection.hidden === true}
          aria-label="Hidden formula"
          title="Hidden formula"
          onChange={(event) => applyProtectionPatch({ hiddenFormula: event.target.checked })}
        />
        <span>Hidden formula</span>
      </label>
    </div>
  )

  const body = {
    number: renderNumber,
    alignment: renderAlignment,
    font: renderFont,
    border: renderBorder,
    fill: renderFill,
    protection: renderProtection,
  }[activeTab]()

  return (
    <div className="fg-excel-format-dialog-backdrop" onMouseDown={onClose}>
      <section
        className="fg-excel-format-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Format Cells"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="fg-excel-format-dialog-header">
          <strong>Format Cells</strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close" title="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>
        <div className="fg-excel-format-tabs" role="tablist" aria-label="Format sections">
          {FORMAT_CELLS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              data-active={activeTab === tab ? 'true' : 'false'}
              aria-selected={activeTab === tab}
              title={`${tab.charAt(0).toUpperCase()}${tab.slice(1)} formatting`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase()}{tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="fg-excel-format-preview" aria-label="Format sample">
          <span>Sample</span>
          <div className="fg-excel-format-preview-cell" style={previewStyle}>
            {previewText}
          </div>
        </div>
        {body}
        <footer className="fg-excel-format-footer">
          <button type="button" title="Clear formats" onClick={clearFormats}>Clear</button>
          <button type="button" title="Close Format Cells" onClick={onClose}>Close</button>
        </footer>
      </section>
    </div>
  )
}
